import { type JSX } from 'react';
import { Control, FieldValues, Path, useForm } from 'react-hook-form';

import { SelectOption } from '@/shared/types';
import { FormSelect, FormTextInput, FormDatePicker } from '@/shared/ui';

import { isLatinLetterRegExp, isUkrainianLetterRegExp } from '@/components/Forms/Validation';

import { useQuery } from '@tanstack/react-query';

import { queryKeys } from '@/api/queryKeys';
import api from '@/api';
import { DocumentTypeCode, DocumentFieldName } from '@/api/types';

export interface UniversalDocumentFieldsProps<T extends FieldValues = FieldValues> {
  documentTypeEntities: SelectOption[];
  layout: 'profile' | 'service';
  documentRequired?: boolean;
  documentTypeLabel?: string;
  disabled?: boolean;
  watch: ReturnType<typeof useForm<T>>['watch'];
  control: Control<T>;
  fieldName: Path<T>;
  pathPrefix?: string;
}

export const UniversalDocumentFields = <T extends FieldValues = FieldValues>({
  documentTypeEntities = [],
  fieldName,
  layout,
  documentRequired = false,
  documentTypeLabel = 'Тип документа',
  disabled,
  control,
  watch,
  pathPrefix = '',
}: UniversalDocumentFieldsProps<T>): JSX.Element => {
  const selectedType = watch(fieldName);
  const documentCode = layout === 'profile' ? fieldName : selectedType?.code;

  const { data: orgNpuOptions = [] } = useQuery({
    queryKey: queryKeys.dictionary.authorities(),
    queryFn: () =>
      api.dictionary.getAuthorities().then((r) => (r.data ?? []).map((item) => ({ label: item.name, id: item.id }))),
    enabled: selectedType?.code === DocumentTypeCode.PERMIT,
  });

  const applyLayout = (elements: JSX.Element[], layoutVariant: 'profile' | 'service'): JSX.Element => {
    const items = [
      <FormSelect
        options={documentTypeEntities}
        control={control}
        name={fieldName as Path<T>}
        label={documentTypeLabel}
        key={fieldName}
        required={documentRequired}
        disabled={disabled}
      />,
      ...elements,
    ];

    return (
      <>
        {items.map((element, index) => (
          <div
            key={element.key || index}
            className={
              layoutVariant === 'profile' ? 'base-chapter-template__field' : 'here-will-be-a-specific-classname'
            }
          >
            {element}
          </div>
        ))}
      </>
    );
  };

  switch (selectedType?.code) {
    case DocumentTypeCode.PASSPORT:
      return applyLayout(
        [
          <FormTextInput
            type="text"
            mask="AA999999"
            formatChars={{
              A: isUkrainianLetterRegExp,
              9: /[0-9]/,
            }}
            maxLength={8}
            control={control}
            name={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.serialNumberDoc}` as Path<T>}
            label="Серія та номер"
            placeholder="Вкажіть серію та номер документа"
            key={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.serialNumberDoc}`}
            required
          />,
          <FormTextInput
            control={control}
            name={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.auth_dep}` as Path<T>}
            label="Орган, що видав"
            placeholder="Вкажіть орган, що видав документ"
            key={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.auth_dep}`}
            required
          />,
          <FormDatePicker
            control={control}
            name={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.date}` as Path<T>}
            label="Дата видачі"
            key={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.date}`}
            maxDateToday
            minDateBirth
            required
          />,
        ],
        layout,
      );
    case DocumentTypeCode.ID_CARD:
      return applyLayout(
        [
          <FormTextInput
            control={control}
            mask="999999999"
            maxLength={9}
            name={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.serialNumberDoc}` as Path<T>}
            label="Номер"
            placeholder="Вкажіть номер документа"
            key={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.serialNumberDoc}`}
            required
          />,
          <FormDatePicker
            control={control}
            name={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.date}` as Path<T>}
            label="Дата видачі"
            key={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.date}`}
            maxDateToday
            minDateBirth
            required
          />,
          <FormDatePicker
            control={control}
            name={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.expirationDate}` as Path<T>}
            minDateBirth
            label="Термін дії"
            key={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.expirationDate}`}
            required
          />,
          <FormTextInput
            control={control}
            name={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.auth_dep}` as Path<T>}
            label="Орган, що видав"
            placeholder="Вкажіть орган, що видав документ"
            mask="9999"
            maxLength={4}
            key={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.auth_dep}`}
            required
            inputMode="numeric"
          />,
          <FormTextInput
            control={control}
            name={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.unzr}` as Path<T>}
            label="УНЗР (Унікальний номер запису в реєстрі)"
            placeholder="Вкажіть УНЗР"
            mask="99999999-99999"
            maxLength={14}
            key={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.unzr}`}
            required
            inputMode="numeric"
          />,
        ],
        layout,
      );
    case DocumentTypeCode.INTERNATIONAL_PASSPORT:
      return applyLayout(
        [
          <FormTextInput
            control={control}
            name={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.serialNumberDoc}` as Path<T>}
            label="Серія та номер"
            mask="AA999999"
            formatChars={{
              A: isLatinLetterRegExp,
              9: /[0-9]/,
            }}
            maxLength={8}
            placeholder="Вкажіть серію та номер документа"
            key={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.serialNumberDoc}`}
            required
          />,
          <FormDatePicker
            control={control}
            name={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.date}` as Path<T>}
            maxDateToday
            minDateBirth
            label="Дата видачі"
            key={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.date}`}
            required
          />,
          <FormDatePicker
            control={control}
            name={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.expirationDate}` as Path<T>}
            minDateBirth
            label="Термін дії"
            key={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.expirationDate}`}
            required
          />,
          <FormTextInput
            control={control}
            name={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.auth_dep}` as Path<T>}
            label="Орган, що видав"
            placeholder="Вкажіть орган, що видав документ"
            mask="9999"
            maxLength={4}
            key={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.auth_dep}`}
            required
            inputMode="numeric"
          />,
          <FormTextInput
            control={control}
            name={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.unzr}` as Path<T>}
            label="УНЗР (Унікальний номер запису в реєстрі)"
            placeholder="Вкажіть УНЗР"
            mask="99999999-99999"
            maxLength={14}
            key={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.unzr}`}
            required
            inputMode="numeric"
          />,
        ],
        layout,
      );
    case DocumentTypeCode.RESIDENCE:
      return applyLayout(
        [
          <FormTextInput
            control={control}
            name={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.serialNumberDoc}` as Path<T>}
            mask="999999999"
            maxLength={9}
            placeholder="Вкажіть номер документа"
            label="Номер"
            key={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.serialNumberDoc}`}
            required
          />,
          <FormDatePicker
            control={control}
            name={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.date}` as Path<T>}
            maxDateToday
            minDateBirth
            label="Дата видачі"
            key={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.date}`}
            required
          />,
          <FormDatePicker
            control={control}
            name={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.expirationDate}` as Path<T>}
            minDateBirth
            label="Термін дії"
            key={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.expirationDate}`}
            required
          />,
          <FormTextInput
            control={control}
            name={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.auth_dep}` as Path<T>}
            placeholder="Вкажіть орган, що видав документ"
            label="Орган, що видав"
            mask="9999"
            maxLength={4}
            key={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.auth_dep}`}
            required
            inputMode="numeric"
          />,
          <FormTextInput
            control={control}
            name={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.unzr}` as Path<T>}
            label="УНЗР (Унікальний номер запису в реєстрі)"
            placeholder="Вкажіть УНЗР"
            mask="99999999-99999"
            maxLength={14}
            key={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.unzr}`}
            required
            inputMode="numeric"
          />,
        ],
        layout,
      );
    case DocumentTypeCode.OLD_RESIDENCE:
      return applyLayout(
        [
          <FormTextInput
            control={control}
            name={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.serialNumberDoc}` as Path<T>}
            label="Серія та номер"
            mask="AA999999"
            formatChars={{
              A: isUkrainianLetterRegExp,
              9: /[0-9]/,
            }}
            maxLength={8}
            placeholder="Вкажіть серію та номер документа"
            key={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.serialNumberDoc}`}
            required
          />,
          <FormTextInput
            control={control}
            name={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.auth_dep}` as Path<T>}
            placeholder="Вкажіть орган, що видав документ"
            label="Орган, що видав"
            key={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.auth_dep}`}
            required
          />,
          <FormDatePicker
            control={control}
            name={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.date}` as Path<T>}
            maxDateToday
            minDateBirth
            label="Дата видачі"
            key={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.date}`}
            required
          />,
        ],
        layout,
      );
    case DocumentTypeCode.TEMPORARY_RESIDENCE:
      return applyLayout(
        [
          <FormTextInput
            control={control}
            name={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.serialNumberDoc}` as Path<T>}
            label="Номер"
            mask="999999999"
            maxLength={9}
            placeholder="Вкажіть номер документа"
            key={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.serialNumberDoc}`}
            required
          />,
          <FormDatePicker
            control={control}
            name={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.date}` as Path<T>}
            maxDateToday
            minDateBirth
            label="Дата видачі"
            key={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.date}`}
            required
          />,
          <FormDatePicker
            control={control}
            name={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.expirationDate}` as Path<T>}
            minDateBirth
            label="Термін дії"
            key={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.expirationDate}`}
            required
          />,
          <FormTextInput
            control={control}
            name={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.auth_dep}` as Path<T>}
            label="Орган, що видав"
            placeholder="Вкажіть орган, що видав документ"
            mask="9999"
            maxLength={4}
            key={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.auth_dep}`}
            required
            inputMode="numeric"
          />,
          <FormTextInput
            control={control}
            name={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.unzr}` as Path<T>}
            label="УНЗР (Унікальний номер запису в реєстрі)"
            placeholder="Вкажіть УНЗР"
            mask="99999999-99999"
            maxLength={14}
            key={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.unzr}`}
            required
            inputMode="numeric"
          />,
        ],
        layout,
      );
    case DocumentTypeCode.VITYAG_TERITORIALNOYI_GROMADI:
      return applyLayout(
        [
          <FormTextInput
            control={control}
            name={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.serialNumberDoc}` as Path<T>}
            label="Номер"
            placeholder="Вкажіть номер документа"
            key={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.serialNumberDoc}`}
            required={layout === 'service'}
          />,
          <FormDatePicker
            control={control}
            name={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.date}` as Path<T>}
            maxDateToday
            minDateBirth
            label="Дата формування"
            key={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.date}`}
            required={layout === 'service'}
          />,
          <FormTextInput
            control={control}
            name={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.auth_dep}` as Path<T>}
            label="Орган, що видав"
            placeholder="Вкажіть орган, що видав документ"
            key={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.auth_dep}`}
            required={layout === 'service'}
          />,
        ],
        layout,
      );
    case DocumentTypeCode.CERT_IDP:
      return applyLayout(
        [
          <FormTextInput
            control={control}
            name={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.serialNumberDoc}` as Path<T>}
            label="Номер"
            placeholder="Вкажіть номер документа"
            key={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.serialNumberDoc}`}
            required={layout === 'service'}
          />,
          <FormDatePicker
            control={control}
            name={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.date}` as Path<T>}
            maxDateToday
            minDateBirth
            label="Дата видачі"
            key={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.date}`}
            required={layout === 'service'}
          />,
        ],
        layout,
      );
    case DocumentTypeCode.LEGAL_RESIDENCE:
      return applyLayout(
        [
          <FormTextInput
            control={control}
            name={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.serialNumberDoc}` as Path<T>}
            label="Номер"
            placeholder="Вкажіть номер документа"
            key={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.serialNumberDoc}`}
            required={layout === 'service'}
          />,
          <FormTextInput
            control={control}
            name={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.auth_dep}` as Path<T>}
            label="Орган, що видав"
            placeholder="Вкажіть орган, що видав документ"
            key={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.auth_dep}`}
            required={layout === 'service'}
          />,
          <FormDatePicker
            control={control}
            name={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.date}` as Path<T>}
            label="Дата формування"
            key={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.date}`}
            maxDateToday
            minDateBirth
            required={layout === 'service'}
          />,
          <FormDatePicker
            control={control}
            name={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.expirationDate}` as Path<T>}
            minDateBirth
            label="Термін дії"
            key={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.expirationDate}`}
            required={layout === 'service'}
          />,
        ],
        layout,
      );

    case DocumentTypeCode.LEASE_AGREEMENT:
      return applyLayout(
        [
          <FormTextInput
            control={control}
            name={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.serialNumberDoc}` as Path<T>}
            label="Номер"
            placeholder="Вкажіть номер документа"
            key={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.serialNumberDoc}`}
          />,
          <FormTextInput
            control={control}
            name={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.indexNumber}` as Path<T>}
            label="Індексний номер"
            placeholder="Вкажіть індексний номер"
            key={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.indexNumber}`}
          />,
          <FormDatePicker
            control={control}
            name={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.date}` as Path<T>}
            label="Дата підписання"
            key={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.date}`}
            maxDateToday
            minDateBirth
          />,
          <FormDatePicker
            control={control}
            name={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.expirationDate}` as Path<T>}
            minDateToday
            label="Строк оренди"
            key={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.expirationDate}`}
          />,
        ],
        layout,
      );
    case DocumentTypeCode.EXRACT_PRPR:
      return applyLayout(
        [
          <FormTextInput
            control={control}
            name={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.serialNumberDoc}` as Path<T>}
            label="Номер запису про право власності"
            placeholder="Вкажіть номер запису"
            key={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.serialNumberDoc}`}
          />,
          <FormTextInput
            control={control}
            name={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.indexNumber}` as Path<T>}
            label="Індексний номер"
            placeholder="Вкажіть індексний номер"
            key={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.indexNumber}`}
          />,
          <FormDatePicker
            control={control}
            name={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.date}` as Path<T>}
            label="Дата видачі"
            key={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.date}`}
            maxDateToday
            minDateBirth
            required={layout === 'service'}
          />,
          <FormTextInput
            control={control}
            name={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.auth_dep}` as Path<T>}
            label="Ким сформовано"
            placeholder="Вкажіть ким сформовано"
            key={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.auth_dep}`}
          />,
        ],
        layout,
      );
    case DocumentTypeCode.OWNERSHIP_CERTIFICATE:
      return applyLayout(
        [
          <FormTextInput
            control={control}
            name={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.serialNumberDoc}` as Path<T>}
            label="Серія та номер"
            placeholder="Вкажіть серію та номер документа"
            key={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.serialNumberDoc}`}
          />,
          <FormTextInput
            control={control}
            name={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.indexNumber}` as Path<T>}
            label="Індексний номер"
            placeholder="Вкажіть індексний номер"
            key={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.indexNumber}`}
          />,
          <FormDatePicker
            control={control}
            name={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.date}` as Path<T>}
            label="Дата видачі"
            key={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.date}`}
            maxDateToday
            minDateBirth
          />,
          <FormTextInput
            control={control}
            name={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.auth_dep}` as Path<T>}
            label="Орган видачі"
            placeholder="Вкажіть орган видачі"
            key={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.auth_dep}`}
            required={layout === 'service'}
          />,
        ],
        layout,
      );
    case DocumentTypeCode.CERT_MARRIAGE:
      return applyLayout(
        [
          <FormTextInput
            control={control}
            name={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.serialNumberDoc}` as Path<T>}
            label="Серія та номер"
            placeholder="Вкажіть серію та номер документа"
            key={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.serialNumberDoc}`}
          />,
          <FormDatePicker
            control={control}
            name={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.date}` as Path<T>}
            label="Дата видачі"
            key={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.date}`}
            maxDateToday
            minDateBirth
          />,
          <FormTextInput
            control={control}
            name={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.auth_dep}` as Path<T>}
            label="Орган, що видав"
            placeholder="Вкажіть орган, що видав документ"
            key={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.auth_dep}`}
          />,
        ],
        layout,
      );
    case DocumentTypeCode.INSURANCE_POLICY:
      return applyLayout(
        [
          <FormTextInput
            control={control}
            name={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.serialNumberDoc}` as Path<T>}
            label="Серія та номер"
            placeholder="Вкажіть серію та номер документа"
            key={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.serialNumberDoc}`}
          />,
          <FormDatePicker
            control={control}
            name={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.date}` as Path<T>}
            label="Дата видачі"
            key={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.date}`}
            maxDateToday
            minDateBirth
          />,
          <FormDatePicker
            control={control}
            name={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.expirationDate}` as Path<T>}
            label="Термін дії"
            key={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.expirationDate}`}
            minDateToday
            required={layout === 'service'}
          />,
        ],
        layout,
      );
    case DocumentTypeCode.EXRACT_PRPR:
      return applyLayout(
        [
          <FormTextInput
            control={control}
            name={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.serialNumberDoc}` as Path<T>}
            label="Номер запису про право власності"
            placeholder="Вкажіть номер документа"
            key={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.serialNumberDoc}`}
            required={layout === 'service'}
          />,
          <FormTextInput
            control={control}
            name={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.indexNumber}` as Path<T>}
            label="Індексний номер"
            placeholder="Вкажіть індексний номер"
            key={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.indexNumber}`}
            required={layout === 'service'}
          />,
          <FormDatePicker
            control={control}
            name={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.date}` as Path<T>}
            label="Дата видачі"
            key={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.date}`}
            maxDateToday
            minDateBirth
            required={layout === 'service'}
          />,
          <FormTextInput
            control={control}
            name={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.auth_dep}` as Path<T>}
            label="Ким сформовано"
            placeholder="Вкажіть ким сформовано"
            key={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.auth_dep}`}
            required={layout === 'service'}
          />,
        ],
        layout,
      );
    case DocumentTypeCode.POWER_OF_ATTORNEY:
      return applyLayout(
        [
          <FormTextInput
            control={control}
            name={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.serialNumberDoc}` as Path<T>}
            label="Номер"
            placeholder="Вкажіть номер документа"
            key={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.serialNumberDoc}`}
            required={layout === 'service'}
          />,
          <FormDatePicker
            control={control}
            name={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.date}` as Path<T>}
            label="Дата видачі"
            key={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.date}`}
            maxDateToday
            minDateBirth
            required={layout === 'service'}
          />,
          <FormDatePicker
            control={control}
            name={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.expirationDate}` as Path<T>}
            label="Термін дії"
            key={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.expirationDate}`}
            minDateBirth
            required={layout === 'service'}
          />,
        ],
        layout,
      );
    case DocumentTypeCode.PERMIT:
      return applyLayout(
        [
          <FormTextInput
            control={control}
            name={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.serialNumberDoc}` as Path<T>}
            label="Номер дозволу"
            placeholder="Вкажіть номер документа"
            key={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.serialNumberDoc}`}
            required={layout === 'service'}
          />,
          <FormDatePicker
            control={control}
            name={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.date}` as Path<T>}
            label="Дата видачі дозволу"
            key={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.date}`}
            maxDateToday
            minDateBirth
            required={layout === 'service'}
          />,
          <FormDatePicker
            control={control}
            name={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.expirationDate}` as Path<T>}
            label="Термін дії"
            key={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.expirationDate}`}
            minDateBirth
          />,
          <FormSelect
            control={control}
            name={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.auth_dep}` as Path<T>}
            label="Орган, який видав дозвіл"
            key={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.auth_dep}`}
            options={orgNpuOptions ?? []}
            required={layout === 'service'}
          />,
        ],
        layout,
      );
    case DocumentTypeCode.EXPERT_RESEARCH:
      return applyLayout(
        [
          <FormTextInput
            control={control}
            name={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.auth_dep}` as Path<T>}
            label="Орган видачі"
            placeholder="Вкажіть орган видачі"
            key={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.auth_dep}`}
            required={layout === 'service'}
          />,
          <FormDatePicker
            control={control}
            name={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.date}` as Path<T>}
            label="Дата видачі"
            key={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.date}`}
            maxDateToday
            minDateBirth
            required={layout === 'service'}
          />,
        ],
        layout,
      );
    case DocumentTypeCode.CERTIFICATION:
      return applyLayout(
        [
          <FormTextInput
            control={control}
            name={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.org}` as Path<T>}
            label="Назва організації"
            placeholder="Вкажіть назву організації"
            key={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.org}`}
            required={layout === 'service'}
          />,
          <FormTextInput
            control={control}
            name={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.serialNumberDoc}` as Path<T>}
            label="Серія та номер"
            placeholder="Вкажіть серію та номер документа"
            key={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.serialNumberDoc}`}
            required={layout === 'service'}
          />,
          <FormTextInput
            control={control}
            name={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.auth_dep}` as Path<T>}
            label="Орган видачі"
            placeholder="Вкажіть орган видачі"
            key={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.auth_dep}`}
            required={layout === 'service'}
          />,
          <FormDatePicker
            control={control}
            name={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.date}` as Path<T>}
            label="Дата видачі"
            key={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.date}`}
            maxDateToday
            minDateBirth
            required={layout === 'service'}
          />,
          <FormDatePicker
            control={control}
            name={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.expirationDate}` as Path<T>}
            label="Дійсний до"
            key={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.expirationDate}`}
            minDateToday
          />,
        ],
        layout,
      );
    case DocumentTypeCode.WORK_REFERENCE:
      return applyLayout(
        [
          <FormTextInput
            control={control}
            name={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.serialNumberDoc}` as Path<T>}
            label="Серія та номер"
            placeholder="Вкажіть серію та номер документа"
            key={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.serialNumberDoc}`}
            required={layout === 'service'}
          />,
          <FormTextInput
            control={control}
            name={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.auth_dep}` as Path<T>}
            label="Орган видачі"
            placeholder="Вкажіть орган видачі"
            key={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.auth_dep}`}
            required={layout === 'service'}
          />,
          <FormDatePicker
            control={control}
            name={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.date}` as Path<T>}
            label="Дата видачі"
            key={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.date}`}
            maxDateToday
            minDateBirth
            required={layout === 'service'}
          />,
          <FormDatePicker
            control={control}
            name={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.expirationDate}` as Path<T>}
            label="Термін дії"
            key={`${pathPrefix + '.'}documents.${documentCode}.info.${DocumentFieldName.expirationDate}`}
            minDateToday
          />,
        ],
        layout,
      );
    default:
      return applyLayout([], layout);
  }
};
